import { Link } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

interface ActivityItem {
  type: string;
  ref_id: number;
  title: string;
  detail: string;
  property_name: string;
  created_at: string;
  href: string;
}

const ICON_FOR_TYPE: Record<string, string> = {
  booking: '📨', message: '💬', maintenance: '🔧', review: '⭐', supply: '🛒',
};

const LAST_VIEWED_KEY = 'openbnb_activity_last_viewed';

function relTime(iso: string): string {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function BellIcon({ hasNew }: { hasNew: boolean }) {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      {hasNew
        ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      }
    </svg>
  );
}

function NotificationDropdown({ items, unreadCount, onClose }: {
  items: ActivityItem[]; unreadCount: number; onClose: () => void;
}) {
  return (
    <div className="absolute right-0 top-10 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <span className="font-semibold text-sm text-gray-900 dark:text-white">Activity</span>
        {unreadCount > 0 && (
          <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-blue-500 text-white">{unreadCount} new</span>
        )}
      </div>
      {items.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-gray-400 dark:text-gray-500">No recent activity</div>
      ) : (
        <ul>
          {items.slice(0, 5).map((item, i) => (
            <li key={i}>
              <Link
                to={item.href}
                onClick={onClose}
                className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <span className="text-base mt-0.5 shrink-0" aria-hidden="true">
                  {ICON_FOR_TYPE[item.type] ?? '🔔'}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.title}</div>
                  {item.detail && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.detail}</div>
                  )}
                  <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    {item.property_name} · {relTime(item.created_at)}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
      <div className="border-t border-gray-100 dark:border-gray-700">
        <Link
          to="/app/activity"
          onClick={onClose}
          className="block w-full px-4 py-2.5 text-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        >
          View all activity →
        </Link>
      </div>
    </div>
  );
}

interface HeaderProps {
  onMenuToggle?: () => void;
}

function SunIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
    </svg>
  );
}

export function Header({ onMenuToggle }: HeaderProps) {
  const { user, logout } = useAuth();
  const { isDark, toggle } = useTheme();

  const [showNotifs, setShowNotifs] = useState(false);
  const [notifItems, setNotifItems] = useState<ActivityItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const bellRef = useRef<HTMLDivElement>(null);

  const loadActivity = useCallback(async () => {
    try {
      const r = await fetch('/api/owner/activity?limit=10', { credentials: 'include' });
      if (!r.ok) return;
      const data = await r.json() as { items: ActivityItem[] };
      const fetchedItems = data.items ?? [];
      setNotifItems(fetchedItems);
      const lastViewed = localStorage.getItem(LAST_VIEWED_KEY) ?? '1970-01-01T00:00:00Z';
      setUnreadCount(fetchedItems.filter(i => new Date(i.created_at) > new Date(lastViewed)).length);
    } catch {
      // ignore fetch errors
    }
  }, []);

  useEffect(() => {
    loadActivity();
    const interval = setInterval(loadActivity, 2 * 60 * 1000);
    const onRefresh = () => loadActivity();
    document.addEventListener('refreshSidebarBadges', onRefresh);
    return () => {
      clearInterval(interval);
      document.removeEventListener('refreshSidebarBadges', onRefresh);
    };
  }, [loadActivity]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showNotifs) return;
    const handler = (e: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
        setShowNotifs(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showNotifs]);

  const handleBellClick = () => {
    if (!showNotifs) {
      // Mark all as viewed when opening the dropdown
      localStorage.setItem(LAST_VIEWED_KEY, new Date().toISOString());
      setUnreadCount(0);
    }
    setShowNotifs(v => !v);
  };

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-30">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center gap-3">
            {/* Hamburger — only on mobile */}
            <button
              onClick={onMenuToggle}
              className="lg:hidden text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white p-1 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              aria-label="Open navigation menu"
              aria-expanded={false}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <Link to="/app/dashboard" className="flex items-center">
              <h1 className="text-xl font-bold text-blue-600 dark:text-blue-400">OpenBNB</h1>
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-gray-600 dark:text-gray-300">
              {user?.firstName} {user?.lastName}
            </span>
            <span className="hidden sm:inline-flex badge bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200">
              {user?.role.replace('_', ' ')}
            </span>
            {!user?.email_verified && (
              <span className="hidden sm:block text-xs text-yellow-800 badge bg-yellow-50 dark:bg-yellow-900/30 dark:text-yellow-400">
                Unverified
              </span>
            )}

            {/* Dark mode toggle */}
            <button
              onClick={toggle}
              className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <SunIcon /> : <MoonIcon />}
            </button>

            {/* Notification bell */}
            <div ref={bellRef} className="relative">
              <button
                onClick={handleBellClick}
                className="relative p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label={`Activity feed${unreadCount > 0 ? `, ${unreadCount} new` : ''}`}
              >
                <BellIcon hasNew={unreadCount > 0} />
                {unreadCount > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 text-[10px] font-bold min-w-[1rem] h-4 flex items-center justify-center rounded-full bg-red-500 text-white px-0.5"
                    aria-hidden="true"
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              {showNotifs && (
                <NotificationDropdown
                  items={notifItems}
                  unreadCount={unreadCount}
                  onClose={() => setShowNotifs(false)}
                />
              )}
            </div>

            <button
              onClick={() => logout()}
              className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
